import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { MANAGER_ID, GROOMER_ID, Option, OWNER_ID } from '@spike/model';
import clsx from 'clsx';
import { MobileDivider } from 'components/UI/MobileDivider';
import { useAuth, useMasterData } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import Staff from 'model/Staff';
import { FunctionComponent, useMemo, useState } from 'react';
import Section from '../UI/Section';
import SelectLocation, { Location } from './UI/SelectLocation';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';

interface Props {
    staff: Staff;
    onChange: (staffUpdated: any) => void;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            height: '100%'
        },
        title: {
            [theme.breakpoints.down('lg')]: {
                fontSize: 20,
                fontWeight: 600
            }
        },
        bodySection: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '0px 16px',

            [theme.breakpoints.up('md')]: {
                paddingLeft: '37px',
                paddingRight: '33px',
                paddingBottom: '29px'
            },
            [theme.breakpoints.up('xl')]: {
                paddingLeft: '49px',
                paddingRight: '43px',
                paddingBottom: '38px'
            }
        },
        description: {
            color: '#7A7A7A',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '18px',
            paddingTop: '26px'
        },
        error: {
            color: '#ef4f57',
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '18px',
            paddingTop: '12px'
        },
        allContainer: {
            display: 'flex',
            alignItems: 'center',
            paddingTop: '4px'
        },
        locationsContainer: {
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: '24px'
        },
        viewAllContainer: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer'
        },
        viewAllIcon: {
            color: '#000',
            width: '16px',
            height: '16px',
            marginLeft: '8px'
        },
        viewAllLabel: {
            color: '#000',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '100%'
        }
    })
);

const ownerOrManager = [OWNER_ID, MANAGER_ID];

const VIEW_ALL_LIMIT = 5;

export const Locations: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const auth = useAuth();
    const masterData = useMasterData();

    const isOwnerOrManager = useMemo(() => ownerOrManager.includes(auth.user?.role?.id ?? 0), [auth.user?.role?.id]);
    const isNewStaff = props.staff.id === undefined;

    const locations: Array<Option<number>> = useMemo(
        () =>
            organizationMarketplaces
                .filter(om => om.active)
                .map(om => ({
                    id: om.id,
                    name: om.name
                }))
                .sort((om, otherOm) => om.name.toLocaleLowerCase().localeCompare(otherOm.name.toLocaleLowerCase())),
        [organizationMarketplaces, auth.marketplaces]
    );

    const [viewAllExpanded, setViewAllExpanded] = useState(false);

    const showAllSelector = useMemo(() => locations.length > 1, [locations]);

    const isSelectedAll = useMemo(
        () => locations.every(m => props.staff.worksAt.filter(w => !w.toDelete).some(w => w.marketplaceId === m.id)),
        [props.staff, locations]
    );

    const handleSelectAll = (selected: boolean) => {
        if (selected) {
            props.onChange({
                ...props.staff,
                worksAt: [
                    ...props.staff.worksAt.map(w => ({ ...w, toDelete: false })),
                    ...locations
                        .filter(l => !props.staff.worksAt.some(w => w.marketplaceId === l.id))
                        .map(l => ({
                            marketplaceId: l.id,
                            staffId: props.staff.id,
                            active: true,
                            toDelete: false
                        }))
                ]
            });
        } else {
            props.onChange({
                ...props.staff,
                worksAt: [
                    ...props.staff.worksAt
                        .filter(w => w.id)
                        .map(w => ({
                            ...w,
                            toDelete:
                                w.marketplaceId === auth.marketplaceId ? props.staff.id !== auth.user?.staffId : true
                        }))
                ]
            });
        }
    };

    const handleSelect = (location: Location) => {
        if (props.staff.worksAt.some(w => w.marketplaceId === location.marketplace.id)) {
            props.onChange({
                ...props.staff,
                worksAt: props.staff.worksAt.map(w =>
                    w.marketplaceId === location.marketplace.id
                        ? {
                              ...w,
                              roleId: location.roleId,
                              toDelete: !w.toDelete
                          }
                        : w
                )
            });
        } else {
            props.onChange({
                ...props.staff,
                worksAt: [
                    ...props.staff.worksAt,
                    {
                        marketplaceId: location.marketplace.id,
                        staffId: props.staff.id,
                        roleId: location.roleId,
                        active: true,
                        toDelete: false
                    }
                ]
            });
        }
    };

    const handleUnselect = (marketplaceId: number) => {
        const work = props.staff.worksAt.find(w => w.marketplaceId === marketplaceId)!;

        if (work.id) {
            props.onChange({
                ...props.staff,
                worksAt: props.staff.worksAt.map(w =>
                    w.marketplaceId === marketplaceId ? { ...w, toDelete: true } : w
                )
            });
        } else {
            props.onChange({
                ...props.staff,
                worksAt: props.staff.worksAt.filter(w => w.marketplaceId !== marketplaceId)
            });
        }
    };

    const changeHandler = (updatedLocation: Location) => {
        props.onChange({
            ...props.staff,
            role:
                updatedLocation.marketplace.id === auth.marketplaceId
                    ? masterData.roles.find(r => r.id === updatedLocation.roleId)
                    : props.staff.role,
            worksAt: props.staff.worksAt.map(w =>
                w.marketplaceId === updatedLocation.marketplace.id
                    ? {
                          ...w,
                          roleId: updatedLocation.roleId
                      }
                    : w
            )
        });
    };

    const allLocationsSelector = (
        <div className={classes.allContainer}>
            <SelectLocation
                location={{ marketplace: { id: -1, name: `All Locations (${locations.length})` } }}
                selected={isSelectedAll}
                disabledLocation={!isOwnerOrManager}
                hideRole={true}
                onSelect={() => handleSelectAll(true)}
                onUnselect={() => handleSelectAll(false)}
            />
        </div>
    );

    const viewAll = (
        <div
            className={classes.viewAllContainer}
            onClick={() => setViewAllExpanded(prev => !prev)}
        >
            <span className={classes.viewAllLabel}>View all</span>
            <FontAwesomeIcon
                icon={viewAllExpanded ? faChevronUp : faChevronDown}
                className={classes.viewAllIcon}
            />
        </div>
    );

    return (
        <div className={clsx(classes.container, props.className)}>
            <MobileDivider />
            <Section
                title="Locations & Permissions"
                {...props}
                titleClassName={classes.title}
            >
                <div className={classes.bodySection}>
                    <span className={classes.description}>
                        Choose the locations & permissions assigned to this team member.
                    </span>
                    {props.staff.worksAt.filter(w => !w.toDelete).length === 0 && (
                        <span className={classes.error}>At least one location must be selected.</span>
                    )}
                    {props.staff.worksAt.filter(w => !w.toDelete).some(w => !w.roleId) && (
                        <span className={classes.error}>All selected locations must have a role assigned.</span>
                    )}

                    {showAllSelector && allLocationsSelector}
                    <div className={classes.locationsContainer}>
                        {locations
                            .slice(0, viewAllExpanded ? locations.length : VIEW_ALL_LIMIT)
                            .map(l => {
                                const w = props.staff.worksAt.find(w => w.marketplaceId === l.id);
                                return {
                                    location: {
                                        marketplace: { ...l },
                                        roleId: w?.roleId
                                    },
                                    selected: w ? !w.toDelete : false
                                };
                            })
                            .map(d => (
                                <SelectLocation
                                    key={d.location.marketplace.id}
                                    location={d.location}
                                    selected={d.selected}
                                    disabledLocation={
                                        !isOwnerOrManager ||
                                        (d.location.marketplace.id === auth.marketplaceId &&
                                            (props.staff.id === auth.user?.staffId || isNewStaff))
                                    }
                                    disableRole={!isOwnerOrManager}
                                    hideBottomBorder={locations.length === 1}
                                    onSelect={handleSelect}
                                    onUnselect={handleUnselect}
                                    onChange={changeHandler}
                                />
                            ))}
                    </div>
                    {locations.length > VIEW_ALL_LIMIT && viewAll}
                </div>
            </Section>
        </div>
    );
};

export default Locations;
